import { useEffect, useRef, useState } from 'react';

export default function MarketingChallenge() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const options = [
    {
      title: 'ALLES SELBST ERLEDIGEN?',
      description: 'Das ist in Ordnung, wenn man wenig zu tun hat.\n\nAber wenn man schon ziemlich überlastet ist ... dann ist das keine praktikable Lösung.',
      icon: 'ri-user-line'
    },
    {
      title: 'NEUES PERSONAL?',
      description: 'Gute Mitarbeiter zu finden ist nicht einfach.\nSchulung und Einarbeitung sind kostspielig.\nUnd selbst wenn man den oder die Richtige für die Stelle findet...\nMan ist immer noch von einer einzigen Person abhängig!',
      icon: 'ri-team-line'
    },
    {
      title: 'EINE AGENTUR BEAUFTRAGEN?',
      description: 'Sie haben kein Marketingbudget von Zehntausenden von Euro? Dann wird Ihr Account möglicherweise vom Praktikanten des Assistenten des Assistenten betreut. Nicht gerade optimal.',
      icon: 'ri-building-line'
    }
  ];

  const benefits = [
    {
      title: 'GARANTIE',
      description: 'Wir gewinnen nur, wenn Sie gewinnen. Das ist die Grundlage einer guten Partnerschaft.\n\nSie tragen nicht das gesamte Risiko, sondern wir teilen es.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'ERGEBNISSE',
      description: 'Unser oberstes Ziel ist es, Ihnen Ergebnisse zu liefern.\nTaten statt Worte.',
      icon: 'ri-line-chart-line'
    },
    {
      title: 'LOKAL',
      description: 'Wir sind keine anonyme Geheimagentur.\n\nWir sind ein lokales Unternehmen, daher können Sie uns erreichen, wenn Sie uns brauchen.',
      icon: 'ri-map-pin-line'
    },
    {
      title: 'SPEZIALISIERT',
      description: 'Vieles kann man ... aber nichts richtig. Spezialisierung zahlt sich aus.\nDeshalb arbeiten wir mit Branchen, die wir kennen, um Ergebnisse zu garantieren.',
      icon: 'ri-focus-line'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={sectionRef}>
        {/* Opening Statement */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Marketing ist wichtig ...
          </h2>
          <p className="text-2xl md:text-3xl text-gray-700 font-medium">
            ... aber auf Ihrer To-do-Liste stehen schon 101 Sachen.
          </p>
          <p className="text-2xl md:text-3xl text-gray-700 font-medium mt-2">
            Und auch alle sind wichtig!
          </p>
        </div>

        {/* Question */}
        <div className="text-center mb-16">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
            Wie optimieren Sie also Ihr Marketing?
          </h3>
        </div>

        {/* Three Options */}
        <div className="grid md:grid-cols-3 gap-8 mb-32">
          {options.map((option, index) => (
            <div
              key={index}
              className={`bg-gray-50 p-8 rounded-2xl border-4 border-gray-300 hover:border-teal-500 transition-all duration-500 ${
                isVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
              style={{
                transitionDelay: `${index * 150}ms`,
                transitionProperty: 'opacity, transform, border-color'
              }}
            >
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl mb-6 mx-auto">
                <i className={`${option.icon} text-3xl text-white`}></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
                {option.title}
              </h4>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line text-center">
                {option.description}
              </p>
            </div>
          ))}
        </div>

        {/* Differentiator Header */}
        <div className="text-center mb-16 mt-24">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
            "Ok ... Was also macht <span className="text-teal-600">uns</span> so anders?"
          </h3>
        </div>

        {/* Four Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-4 border-gray-300 hover:border-teal-500 hover:shadow-xl transition-all duration-500 ${
                isVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
              style={{
                transitionDelay: `${(index + 3) * 150}ms`,
                transitionProperty: 'opacity, transform, border-color, box-shadow'
              }}
            >
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg mb-5 mx-auto">
                <i className={`${benefit.icon} text-2xl text-white`}></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
                {benefit.title}
              </h4>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line text-center">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Contact Button */}
        <div className="text-center mt-12">
          <button
            onClick={scrollToContact}
            className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
          >
            Kontaktieren Sie uns
          </button>
        </div>
      </div>
    </section>
  );
}
